// supervisor-config-drift-reconcile.ts
//
// Moved out of supervisor.ts (V2 phase 02 — the file sat at the 200-line
// budget). N39 drift rule, behaviour unchanged except the key list:
// compares the freshly loaded effective-config against what
// compatibility.json recorded at init. (a) permissions_allow_fallback
// changed => re-apply mission settings from the pinned pack AND record the new
// settings digest in compatibility.json in the same lock (review 04b L6 —
// mission-settings-reapply-from-pinned-pack.ts); (b) any
// OTHER key => `effective_config_changed{keys}` naming key NAMES only (never
// values, per spec: "không giá trị secret"). A string returned to the caller,
// NOT a journal event. V2 §7: the V2 keys are named too, after the first
// three v1 keys (the v1 cap), so they are never cut off by it.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { reapplyMissionSettingsAndDigest } from "./mission-settings-reapply-from-pinned-pack.js";
import type { Compatibility } from "./schemas/compatibility-schema.js";

export interface ReconcileConfigDriftInput {
  controlRoot: string;
  missionWorkspace: string;
  freshPermissionsAllowFallback: boolean;
  freshConfigForKeyDiff: Record<string, unknown>;
  /** V2 §7: the run's `configV2` — only its key names are used. */
  freshConfigV2ForKeyDiff: Record<string, unknown>;
}

/**
 * Reconcile a freshly loaded effective-config against compatibility.json —
 * the N39 drift rule. Returns the list of drift actions taken (for logging /
 * tests), never leaks changed VALUES for non-fallback keys.
 */
export async function reconcileConfigDrift(input: ReconcileConfigDriftInput): Promise<string[]> {
  const compatPath = join(input.controlRoot, "compatibility.json");
  if (!(await pathExists(compatPath))) return [];
  const compat = await readJson<Compatibility>(compatPath);
  const actions: string[] = [];

  if (compat.permissions_allow_fallback !== input.freshPermissionsAllowFallback) {
    const change = `from:${compat.permissions_allow_fallback},to:${input.freshPermissionsAllowFallback}`;
    try {
      await reapplyMissionSettingsAndDigest({
        controlRoot: input.controlRoot,
        missionWorkspace: input.missionWorkspace,
        permissionsAllowFallback: input.freshPermissionsAllowFallback,
      });
      actions.push(`mission_settings_reapplied{${change}}`);
    } catch {
      // Best-effort at supervisor startup: the reapply reads everything before
      // writing, so a read failure writes nothing; a failed write leaves a
      // digest mismatch that doctor's digest check reports.
      actions.push(`mission_settings_reapply_failed{${change}}`);
    }
  }

  const knownCompatKeys = new Set(["permissions_allow_fallback"]);
  const changedKeys = [
    ...Object.keys(input.freshConfigForKeyDiff)
      .filter((k) => !knownCompatKeys.has(k))
      .slice(0, 3),
    ...Object.keys(input.freshConfigV2ForKeyDiff),
  ];
  if (changedKeys.length > 0) {
    // Only KEY NAMES, never values (spec: no secret leakage).
    actions.push(`effective_config_changed{keys:[${changedKeys.join(",")}]}`);
  }

  return actions;
}
